/**
 * Chutt... - Historique & Report Page (merged)
 * Tab 1: Rapport du Jour - Executive summary, KPIs, objectives, report history
 * Tab 2: Historique - Score evolution chart, daily history table, day detail modal
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  Skeleton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  LinearProgress,
  Divider,
  CircularProgress,
  Card,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FlagIcon from '@mui/icons-material/Flag';
import CloseIcon from '@mui/icons-material/Close';
import CalculateIcon from '@mui/icons-material/Calculate';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import ArticleIcon from '@mui/icons-material/Article';
import HistoryIcon from '@mui/icons-material/History';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

import { RepScoreGauge } from '../components/score';
import { MiniKPICard, SentimentBar, ObjectiveCard, PageTransition, ChartTooltip } from '../components/shared';
import GlassCard from '../components/shared/GlassCard';
import ScrollReveal from '../components/shared/ScrollReveal';
import MentionCard from '../components/mentions/MentionCard';
import SentimentCandlestickChart from '../components/charts/SentimentCandlestickChart';
import { analyticsAPI, reportsAPI, objectivesAPI, monitoringAPI, seoAPI } from '../services/api';
import { colors } from '../styles/colors';

// Status colors for report badges
const STATUS_COLORS = {
  EXCELLENT: colors.success,
  BON: colors.info,
  VIGILANCE: colors.warning,
  ATTENTION: colors.warningDark,
  ALERTE: colors.danger,
};

// ---------- Helpers ----------

const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatDateShort = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// ---------- Sub-components ----------

/** Hidden tab panel wrapper */
function TabPanel({ children, value, index, ...other }) {
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// Tooltip uses centralized ChartTooltip from shared

// ---------- Main Component ----------

function HistoriqueReportPage() {
  const { selectedBrand, brands } = useOutletContext();

  // --- Shared state ---
  const [mainTab, setMainTab] = useState(0);

  // --- Report tab state ---
  const reportLoadedOnce = useRef(false);
  const [reportLoading, setReportLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [reportSuccess, setReportSuccess] = useState(null);
  const [latestReport, setLatestReport] = useState(null);
  const [reportHistory, setReportHistory] = useState([]);
  const [objectivesProgress, setObjectivesProgress] = useState(null);
  const [serpData, setSerpData] = useState(null);
  const [campaigns, setCampaigns] = useState([]);

  // Report detail dialog
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDetailOpen, setReportDetailOpen] = useState(false);

  // --- Historique tab state ---
  const histLoadedOnce = useRef(false);
  const [period, setPeriod] = useState(30);
  const [histLoading, setHistLoading] = useState(true);
  const [histError, setHistError] = useState(null);
  const [historyData, setHistoryData] = useState([]);

  // Day detail dialog
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayDetailOpen, setDayDetailOpen] = useState(false);
  const [dayMentions, setDayMentions] = useState([]);
  const [loadingMentions, setLoadingMentions] = useState(false);

  const currentBrand = brands?.find((b) => b.id === selectedBrand);

  // ---------- Report data fetching ----------

  const fetchReports = useCallback(async () => {
    if (!selectedBrand) return;
    if (!reportLoadedOnce.current) setReportLoading(true);
    setReportError(null);

    try {
      const [latestRes, historyRes, progressRes, seoRes, campaignsRes] = await Promise.all([
        reportsAPI.getLatest(selectedBrand).catch(() => ({ data: { report: null } })),
        reportsAPI.getHistory(selectedBrand, 90, 30).catch(() => ({ data: { reports: [] } })),
        objectivesAPI.getProgress(selectedBrand).catch(() => ({ data: null })),
        seoAPI.getSerpResults(selectedBrand).catch(() => ({ data: null })),
        seoAPI.getCampaigns(selectedBrand).catch(() => ({ data: { campaigns: [] } })),
      ]);

      setLatestReport(latestRes.data?.report || null);
      setReportHistory(historyRes.data?.reports || []);
      setObjectivesProgress(progressRes.data || null);
      setSerpData(seoRes.data || null);
      setCampaigns(campaignsRes.data?.campaigns || []);
    } catch (err) {
      console.error('Reports fetch error:', err);
      setReportError('Erreur lors du chargement des rapports.');
    } finally {
      setReportLoading(false);
      reportLoadedOnce.current = true;
    }
  }, [selectedBrand]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // ---------- Historique data fetching ----------

  useEffect(() => {
    const fetchHistory = async () => {
      if (!selectedBrand) return;
      if (!histLoadedOnce.current) setHistLoading(true);
      setHistError(null);

      try {
        const res = await analyticsAPI.getHistory(selectedBrand, period);
        setHistoryData(res.data?.history || []);
      } catch (err) {
        console.error('History fetch error:', err);
        setHistError('Erreur lors du chargement de l\'historique');
      } finally {
        setHistLoading(false);
        histLoadedOnce.current = true;
      }
    };

    fetchHistory();
  }, [selectedBrand, period]);

  // ---------- Report handlers ----------

  const handleGenerateReport = async () => {
    if (!selectedBrand) return;
    setGenerating(true);
    setReportError(null);
    setReportSuccess(null);

    try {
      const res = await reportsAPI.generate(selectedBrand);
      if (res.data?.success) {
        setReportSuccess('Rapport genere avec succes!');
        await fetchReports();
      }
    } catch (err) {
      console.error('Generate report error:', err);
      setReportError('Erreur lors de la generation du rapport.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPdf = async (reportId) => {
    try {
      const res = await reportsAPI.downloadPdf(reportId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `chutt-report-${reportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download PDF error:', err);
      setReportError('Erreur lors du telechargement du PDF.');
    }
  };

  const handleViewReportDetails = async (report) => {
    if (report.full_report) {
      setSelectedReport(report);
      setReportDetailOpen(true);
    } else {
      try {
        const res = await reportsAPI.getById(report.report_id);
        setSelectedReport(res.data?.report || report);
        setReportDetailOpen(true);
      } catch (err) {
        console.error('Fetch report details error:', err);
        setReportError('Erreur lors du chargement des details.');
      }
    }
  };

  // ---------- Historique handlers ----------

  const handleDayClick = async (dayData) => {
    setSelectedDay(dayData);
    setDayDetailOpen(true);
    setLoadingMentions(true);

    try {
      const res = await monitoringAPI.getMentions({
        brand_id: selectedBrand,
        date: dayData.date,
        limit: 100,
      });
      setDayMentions(res.data?.mentions || []);
    } catch (err) {
      console.error('Error fetching day mentions:', err);
      setDayMentions([]);
    } finally {
      setLoadingMentions(false);
    }
  };

  const handleCloseDayDetail = () => {
    setDayDetailOpen(false);
    setSelectedDay(null);
    setDayMentions([]);
  };

  // ---------- Render helpers ----------

  const renderStatusBadge = (status, statusColor) => {
    const color = STATUS_COLORS[status] || statusColor || colors.charcoal;
    return (
      <Chip
        label={status}
        size="small"
        sx={{
          bgcolor: `${color}20`,
          color: color,
          fontWeight: 'bold',
          letterSpacing: '0.5px',
        }}
      />
    );
  };

  const renderTrendIcon = (direction) => {
    if (direction === 'up' || direction === 'positive') {
      return <TrendingUpIcon sx={{ color: colors.success }} />;
    } else if (direction === 'down' || direction === 'negative') {
      return <TrendingDownIcon sx={{ color: colors.danger }} />;
    }
    return <TrendingFlatIcon sx={{ color: colors.warning }} />;
  };

  const getRowTrendIcon = (current, previous) => {
    if (!previous) return <TrendingFlatIcon sx={{ color: colors.neutral }} />;
    if (current > previous) return <TrendingUpIcon sx={{ color: colors.success }} />;
    if (current < previous) return <TrendingDownIcon sx={{ color: colors.danger }} />;
    return <TrendingFlatIcon sx={{ color: colors.neutral }} />;
  };

  // Current / previous score for gauge
  const currentScore = historyData.length > 0
    ? historyData[historyData.length - 1]?.reputation_score
    : 0;
  const previousWeekScore = historyData.length > 7
    ? historyData[historyData.length - 8]?.reputation_score
    : null;

  // ---------- Guard ----------

  if (!selectedBrand) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography sx={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontSize: '1.4rem',
          color: colors.textLight,
        }}>
          Selectionnez une marque pour voir les rapports
        </Typography>
      </Box>
    );
  }

  // =====================================================================
  // RENDER
  // =====================================================================
  return (
    <PageTransition>
    <Box sx={{
      background: `linear-gradient(180deg, ${colors.ivory} 0%, ${colors.ivoryWarm || colors.ivory} 100%)`,
      minHeight: 'calc(100vh - 80px)',
      pb: 4,
      mx: 'auto',
      px: { xs: 2, sm: 4, md: 5 },
    }}>
      {/* Page Header */}
      <ScrollReveal>
        <Box sx={{ pt: 5, pb: 4, mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: { xs: '2.4rem', sm: '3.2rem', md: '4.8rem' },
              fontWeight: 500,
              color: colors.charcoal,
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
            }}>
              Rapports & Historique
            </Typography>
            {currentBrand && (
              <Typography sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '0.9rem',
                color: colors.textMed,
                mt: 1,
              }}>
                {currentBrand.name} — Analyse et suivi de reputation
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Actualiser">
              <span>
                <IconButton onClick={fetchReports} disabled={reportLoading} aria-label="Actualiser les rapports">
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
              onClick={handleGenerateReport}
              disabled={generating}
            >
              {generating ? 'Generation...' : 'Generer un rapport'}
            </Button>
          </Box>
        </Box>
      </ScrollReveal>

      {/* Alerts */}
      {reportError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setReportError(null)}>
          {reportError}
        </Alert>
      )}
      {reportSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setReportSuccess(null)}>
          {reportSuccess}
        </Alert>
      )}
      {histError && mainTab === 1 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {histError}
        </Alert>
      )}

      {/* ============ TABS ============ */}
      <Tabs
        value={mainTab}
        onChange={(_, v) => setMainTab(v)}
        sx={{
          mb: 0,
          '& .MuiTab-root': {
            color: colors.textMed,
            textTransform: 'none',
            fontWeight: 600,
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '0.9rem',
          },
          '& .Mui-selected': { color: colors.primary },
          '& .MuiTabs-indicator': { bgcolor: colors.primary },
        }}
      >
        <Tab icon={<ArticleIcon />} iconPosition="start" label="Rapport du Jour" />
        <Tab icon={<HistoryIcon />} iconPosition="start" label="Historique" />
      </Tabs>

      {/* ============================================================= */}
      {/*  TAB 1 - Rapport du Jour                                      */}
      {/* ============================================================= */}
      <TabPanel value={mainTab} index={0}>
        {reportLoading ? (
          <Box>
            <Skeleton variant="rounded" height={200} sx={{ mb: 2, borderRadius: '20px' }} />
            <Grid container spacing={2}>
              {[1, 2, 3].map((i) => (
                <Grid item xs={12} sm={4} key={i}>
                  <Skeleton variant="rounded" height={110} sx={{ borderRadius: '20px' }} />
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : latestReport ? (
          <Grid container spacing={3}>
            {/* --- Section 1: Executive Summary + PDF + Brand --- */}
            <Grid item xs={12}>
              <GlassCard>
                <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography sx={{
                        fontFamily: '"DM Sans", sans-serif',
                        fontSize: '0.72rem',
                        color: colors.textMed,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        fontWeight: 600,
                      }}>
                        Rapport du {formatDate(latestReport.report_date)}
                      </Typography>
                      <Typography sx={{
                        fontFamily: '"Playfair Display", Georgia, serif',
                        fontSize: '1.4rem',
                        fontWeight: 500,
                        color: colors.charcoal,
                        mt: 0.5,
                      }}>
                        Synthese Executive
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {renderStatusBadge(latestReport.status, latestReport.status_color)}
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={() => handleDownloadPdf(latestReport.report_id)}
                      >
                        Telecharger PDF
                      </Button>
                    </Box>
                  </Box>

                  <Typography sx={{
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: '0.9rem',
                    color: colors.charcoal,
                    lineHeight: 1.8,
                    textAlign: 'justify',
                  }}>
                    {latestReport.summary_text}
                  </Typography>

                  {latestReport.dashboard_summary?.one_liner && (
                    <Typography sx={{
                      fontFamily: '"DM Sans", sans-serif',
                      mt: 2,
                      fontStyle: 'italic',
                      color: colors.textMed,
                      fontSize: '0.85rem',
                    }}>
                      {latestReport.dashboard_summary.one_liner}
                    </Typography>
                  )}
                </Box>
              </GlassCard>
            </Grid>

            {/* --- Section 2: KPI Cards --- */}
            <Grid item xs={6} sm={3}>
              <MiniKPICard
                title="Score de Reputation"
                value={latestReport.reputation_score?.toFixed(0) || 0}
                change={latestReport.score_change || null}
                unit="/100"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniKPICard
                title="NSS"
                value={latestReport.nss?.toFixed(0) || 0}
                change={latestReport.nss_change || null}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniKPICard
                title="Mentions"
                value={latestReport.total_mentions || 0}
                change={latestReport.mentions_change || null}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniKPICard
                title="Portee totale"
                value={latestReport.total_reach?.toLocaleString() || 0}
              />
            </Grid>

            {/* --- Sentiment distribution --- */}
            <Grid item xs={12} md={6}>
              <GlassCard sx={{ height: '100%' }}>
                <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
                <Typography sx={{
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  color: colors.charcoal,
                  mb: 2,
                }}>
                  Distribution du Sentiment
                </Typography>
                <SentimentBar
                  positive={latestReport.positive_count || 0}
                  neutral={latestReport.neutral_count || 0}
                  negative={latestReport.negative_count || 0}
                />
                </Box>
              </GlassCard>
            </Grid>

            {/* --- Section 3: Objectives progress --- */}
            {latestReport.full_report?.objectives_analysis?.has_objectives && (
              <Grid item xs={12} md={6}>
                <GlassCard sx={{ height: '100%' }}>
                  <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <FlagIcon sx={{ color: colors.primary }} />
                    <Typography sx={{
                      fontFamily: '"Playfair Display", Georgia, serif',
                      fontSize: '1.1rem',
                      fontWeight: 500,
                      color: colors.charcoal,
                    }}>
                      Progression des Objectifs
                    </Typography>
                  </Box>

                  {/* Overall bar */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="textSecondary">Progression globale</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: colors.charcoal }}>
                        {latestReport.full_report.objectives_analysis.overall_progress_pct?.toFixed(0)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={latestReport.full_report.objectives_analysis.overall_progress_pct || 0}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: colors.gray100,
                        '& .MuiLinearProgress-bar': { bgcolor: colors.primary },
                      }}
                    />
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                      {latestReport.full_report.objectives_analysis.achieved_count} / {latestReport.full_report.objectives_analysis.total_objectives} atteints
                    </Typography>
                  </Box>

                  {/* Individual objective cards */}
                  <Grid container spacing={1}>
                    {latestReport.full_report.objectives_analysis.objectives?.slice(0, 4).map((obj, idx) => (
                      <Grid item xs={12} sm={6} key={idx}>
                        <ObjectiveCard
                          name={obj.name}
                          progress={obj.progress_pct}
                          current={obj.current}
                          target={obj.target}
                          unit={obj.unit}
                          type={obj.metric_type}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  </Box>
                </GlassCard>
              </Grid>
            )}

            {/* Also show standalone objectives progress when the report has no objectives_analysis */}
            {!latestReport.full_report?.objectives_analysis?.has_objectives && objectivesProgress?.objectives?.length > 0 && (
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${colors.gray200}`, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <FlagIcon sx={{ color: colors.charcoal }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: colors.charcoal }}>
                      Objectifs
                    </Typography>
                  </Box>
                  <Grid container spacing={1}>
                    {objectivesProgress.objectives.slice(0, 4).map((obj, idx) => (
                      <Grid item xs={12} sm={6} key={idx}>
                        <ObjectiveCard
                          name={obj.name}
                          progress={obj.progress_pct}
                          current={obj.current_value}
                          target={obj.target_value}
                          unit={obj.unit}
                          type={obj.metric_type}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
            )}

            {/* --- Section SEO & Influence --- */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${colors.gray200}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <RocketLaunchIcon sx={{ color: colors.accent }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: colors.charcoal }}>
                    SEO & Influence
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  {/* SERP Score */}
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: colors.gray50, borderRadius: 2 }}>
                      <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 800, fontSize: '2rem', color: serpData?.serp_score >= 70 ? colors.success : serpData?.serp_score >= 40 ? colors.warning : serpData?.serp_score != null ? colors.danger : colors.gray400 }}>
                        {serpData?.serp_score != null ? `${serpData.serp_score}%` : '-'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">SERP Score</Typography>
                      {serpData?.top_10_total > 0 && (
                        <Typography variant="caption" sx={{ display: 'block', color: colors.gray500 }}>
                          {serpData.top_10_positive}/{serpData.top_10_total} positifs dans le top 10
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* SERP Sentiment Breakdown */}
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ p: 2, bgcolor: colors.gray50, borderRadius: 2, height: '100%' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: colors.charcoal, mb: 1 }}>
                        Top 10 Google
                      </Typography>
                      {serpData?.results?.length > 0 ? (() => {
                        const top10 = serpData.results.filter(r => r.position <= 10);
                        const pos = top10.filter(r => r.sentiment === 'positive').length;
                        const neg = top10.filter(r => r.sentiment === 'negative').length;
                        const neu = top10.filter(r => r.sentiment === 'neutral').length;
                        const _total = top10.length || 1; // eslint-disable-line no-unused-vars
                        return (
                          <>
                            <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                              {pos > 0 && <Box sx={{ flex: pos, height: 24, bgcolor: colors.success, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ color: 'white', fontSize: '0.7rem', fontWeight: 700 }}>{pos}</Typography></Box>}
                              {neu > 0 && <Box sx={{ flex: neu, height: 24, bgcolor: colors.gray300, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ fontSize: '0.7rem', fontWeight: 700 }}>{neu}</Typography></Box>}
                              {neg > 0 && <Box sx={{ flex: neg, height: 24, bgcolor: colors.danger, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ color: 'white', fontSize: '0.7rem', fontWeight: 700 }}>{neg}</Typography></Box>}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" sx={{ color: colors.success }}>Positif</Typography>
                              <Typography variant="caption" sx={{ color: colors.gray500 }}>Neutre</Typography>
                              <Typography variant="caption" sx={{ color: colors.danger }}>Negatif</Typography>
                            </Box>
                          </>
                        );
                      })() : (
                        <Typography variant="body2" color="textSecondary">Aucun scan SERP</Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* Active Campaigns */}
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ p: 2, bgcolor: colors.gray50, borderRadius: 2, height: '100%' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: colors.charcoal, mb: 1 }}>
                        Campagnes SEO
                      </Typography>
                      {campaigns.length > 0 ? (
                        <>
                          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 700, fontSize: '1.5rem', color: colors.charcoal }}>
                            {campaigns.filter(c => c.status === 'active').length}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">campagnes actives</Typography>
                          {campaigns.slice(0, 2).map((c, idx) => (
                            <Box key={idx} sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: c.status === 'active' ? colors.success : colors.gray400 }} />
                              <Typography variant="caption" sx={{ color: colors.gray600 }}>{c.name}</Typography>
                            </Box>
                          ))}
                        </>
                      ) : (
                        <Typography variant="body2" color="textSecondary">Aucune campagne</Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* Top SERP results preview */}
                  {serpData?.results?.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: colors.charcoal, mb: 1 }}>
                        Top 5 resultats Google
                      </Typography>
                      {serpData.results.slice(0, 5).map((r, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: idx < 4 ? `1px solid ${colors.gray100}` : 'none' }}>
                          <Chip
                            label={`#${r.position}`}
                            size="small"
                            sx={{ width: 36, bgcolor: colors.gray100, fontWeight: 700, fontSize: '0.7rem' }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.charcoal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {r.title}
                            </Typography>
                            <Typography variant="caption" sx={{ color: colors.gray400 }}>{r.domain}</Typography>
                          </Box>
                          <Chip
                            label={r.sentiment === 'positive' ? 'Positif' : r.sentiment === 'negative' ? 'Negatif' : 'Neutre'}
                            size="small"
                            sx={{
                              bgcolor: r.sentiment === 'positive' ? `${colors.success}15` : r.sentiment === 'negative' ? `${colors.danger}15` : `${colors.gray300}30`,
                              color: r.sentiment === 'positive' ? colors.success : r.sentiment === 'negative' ? colors.danger : colors.gray500,
                              fontWeight: 600,
                              fontSize: '0.65rem',
                            }}
                          />
                        </Box>
                      ))}
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>

            {/* --- Section 4: Recent report history table --- */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${colors.gray200}` }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: colors.charcoal, mb: 2 }}>
                  Historique des Rapports
                </Typography>

                {reportHistory.length > 0 ? (
                  <TableContainer sx={{ maxHeight: 340 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.gray50 }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.gray50 }}>Statut</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.gray50 }} align="center">Score</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.gray50 }} align="center">NSS</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.gray50 }} align="center">Mentions</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.gray50 }} align="center">Tendance</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.gray50 }} align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportHistory.map((report) => (
                          <TableRow key={report.report_id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarTodayIcon fontSize="small" sx={{ color: colors.charcoal }} />
                                {formatDateShort(report.report_date)}
                              </Box>
                            </TableCell>
                            <TableCell>{renderStatusBadge(report.status, report.status_color)}</TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" fontWeight="bold">
                                {report.reputation_score?.toFixed(0) || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" fontWeight="bold">
                                {report.nss?.toFixed(0) || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">{report.total_mentions || 0}</TableCell>
                            <TableCell align="center">{renderTrendIcon(report.trend_direction)}</TableCell>
                            <TableCell align="right">
                              <Tooltip title="Voir details">
                                <IconButton size="small" onClick={() => handleViewReportDetails(report)} aria-label="Voir details">
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Telecharger PDF">
                                <IconButton size="small" onClick={() => handleDownloadPdf(report.report_id)} aria-label="Telecharger PDF">
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                    Aucun historique de rapports disponible.
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Aucun rapport disponible
            </Typography>
            <Typography variant="body2">
              Cliquez sur "Generer un rapport" pour creer votre premier rapport de reputation.
            </Typography>
          </Alert>
        )}
      </TabPanel>

      {/* ============================================================= */}
      {/*  TAB 2 - Historique                                            */}
      {/* ============================================================= */}
      <TabPanel value={mainTab} index={1}>
        {/* Period selector */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={(_, val) => val && setPeriod(val)}
            size="small"
          >
            <ToggleButton value={7}>7j</ToggleButton>
            <ToggleButton value={30}>30j</ToggleButton>
            <ToggleButton value={90}>90j</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Grid container spacing={3}>
          {/* Score gauge */}
          <Grid item xs={12} md={4}>
            <Box sx={{ height: 340, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              {histLoading ? (
                <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
              ) : (
                <RepScoreGauge
                  score={currentScore}
                  previousScore={previousWeekScore}
                  size="large"
                  showDetails={true}
                />
              )}
            </Box>
          </Grid>

          {/* Score Evolution LineChart */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${colors.gray200}`, height: 340 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: colors.charcoal }}>
                Evolution du Score
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                Cliquez sur un point pour voir les details du jour
              </Typography>

              {histLoading ? (
                <Skeleton variant="rectangular" height={240} />
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart
                    data={historyData}
                    onClick={(e) => {
                      if (e && e.activePayload && e.activePayload[0]) {
                        handleDayClick(e.activePayload[0].payload);
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <XAxis
                      dataKey="date"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: colors.gray400 }}
                      tickFormatter={(val) => {
                        const d = new Date(val);
                        return `${d.getDate()}/${d.getMonth() + 1}`;
                      }}
                    />
                    <YAxis
                      domain={[0, 100]}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: colors.gray400 }}
                      width={45}
                    />
                    <RechartsTooltip content={<ChartTooltip />} />
                    <ReferenceLine
                      y={75}
                      stroke={colors.success}
                      strokeDasharray="5 5"
                      label={{ value: 'Objectif: 75', position: 'right', fill: colors.success, fontSize: 12 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="reputation_score"
                      stroke={colors.charcoal}
                      strokeWidth={3}
                      dot={{ fill: colors.charcoal, strokeWidth: 2, r: 4, cursor: 'pointer' }}
                      activeDot={{ r: 8, fill: colors.charcoal, cursor: 'pointer' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          {/* Sentiment Candlestick Chart */}
          {!histLoading && historyData.length > 0 && (
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${colors.gray200}` }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: colors.charcoal }}>
                  Sentiment par jour
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                  Mentions positives (haut) et negatives (bas) par jour
                </Typography>
                <SentimentCandlestickChart historyData={historyData} height={240} />
              </Paper>
            </Grid>
          )}

          {/* History Table */}
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${colors.gray200}` }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: colors.charcoal }}>
                Historique Detaille
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                Cliquez sur une ligne pour voir les mentions du jour
              </Typography>

              {histLoading ? (
                <Skeleton variant="rectangular" height={380} />
              ) : (
                <TableContainer sx={{ maxHeight: 440 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.gray50 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.gray50 }}>Score</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.gray50 }}>NSS</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.gray50 }}>Mentions</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.gray50 }}>+/-/O</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.gray50 }}>Trend</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {historyData.slice().reverse().slice(0, 30).map((row, idx) => {
                        const prevRow = historyData[historyData.length - idx - 2];
                        const score = row.reputation_score || 0;
                        return (
                          <TableRow
                            key={row.date}
                            hover
                            onClick={() => handleDayClick(row)}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { bgcolor: `${colors.charcoal}08` },
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarTodayIcon sx={{ fontSize: 16, color: colors.charcoal }} />
                                {row.date}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={Math.round(score)}
                                size="small"
                                sx={{
                                  bgcolor: score >= 75 ? colors.successBg :
                                           score >= 50 ? colors.warningBg : colors.dangerBg,
                                  color: score >= 75 ? colors.success :
                                         score >= 50 ? colors.warning : colors.danger,
                                  fontWeight: 'bold',
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: (row.nss || 0) >= 0 ? colors.success : colors.danger,
                                  fontWeight: 'bold',
                                }}
                              >
                                {row.nss > 0 ? '+' : ''}{Math.round(row.nss || 0)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {row.total_mentions || 0}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                <Chip
                                  label={`+${row.positive_count || 0}`}
                                  size="small"
                                  sx={{ bgcolor: colors.successBg, color: colors.success, fontSize: '0.7rem', height: 20 }}
                                />
                                <Chip
                                  label={`-${row.negative_count || 0}`}
                                  size="small"
                                  sx={{ bgcolor: colors.dangerBg, color: colors.danger, fontSize: '0.7rem', height: 20 }}
                                />
                                <Chip
                                  label={`O${row.neutral_count || 0}`}
                                  size="small"
                                  sx={{ bgcolor: colors.neutralBg, color: colors.neutral, fontSize: '0.7rem', height: 20 }}
                                />
                              </Box>
                            </TableCell>
                            <TableCell>
                              {getRowTrendIcon(score, prevRow?.reputation_score)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* ============================================================= */}
      {/*  DIALOG: Day Detail (Historique tab)                           */}
      {/* ============================================================= */}
      <Dialog
        open={dayDetailOpen}
        onClose={handleCloseDayDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            bgcolor: colors.charcoal,
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">
            Details du {selectedDay && formatDate(selectedDay.date)}
          </Typography>
          <IconButton onClick={handleCloseDayDetail} aria-label="Fermer" sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {selectedDay && (
            <>
              {/* Score Calculation */}
              <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: colors.gray50, borderRadius: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <CalculateIcon sx={{ color: colors.charcoal }} />
                  Calcul du Score
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: colors.charcoal }}>
                        {Math.round(selectedDay.reputation_score || 0)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">Score /100</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2 }}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 'bold',
                          color: (selectedDay.nss || 0) >= 0 ? colors.success : colors.danger,
                        }}
                      >
                        {selectedDay.nss > 0 ? '+' : ''}{Math.round(selectedDay.nss || 0)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">NSS</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: colors.charcoal }}>
                        {selectedDay.total_mentions || 0}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">Mentions</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: colors.charcoal }}>
                        {selectedDay.total_mentions > 0
                          ? Math.round((selectedDay.positive_count / selectedDay.total_mentions) * 100)
                          : 0}%
                      </Typography>
                      <Typography variant="caption" color="textSecondary">Positif</Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Formula */}
                <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                    <strong>NSS</strong> = (Positifs - Negatifs) / Total x 100
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                    = ({selectedDay.positive_count || 0} - {selectedDay.negative_count || 0}) / {selectedDay.total_mentions || 1} x 100 = <strong>{Math.round(selectedDay.nss || 0)}</strong>
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                    <strong>Score</strong> = 50 + (NSS / 2)
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                    = 50 + ({Math.round(selectedDay.nss || 0)} / 2) = <strong>{Math.round(selectedDay.reputation_score || 50)}</strong>
                  </Typography>
                </Box>

                {/* Sentiment breakdown bar */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>Repartition du sentiment</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Box sx={{ flex: selectedDay.positive_count || 1, bgcolor: colors.success, height: 20, borderRadius: 1 }} />
                    <Box sx={{ flex: selectedDay.neutral_count || 1, bgcolor: colors.neutral, height: 20, borderRadius: 1 }} />
                    <Box sx={{ flex: selectedDay.negative_count || 1, bgcolor: colors.danger, height: 20, borderRadius: 1 }} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: colors.success }}>
                      {selectedDay.positive_count || 0} positifs
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.neutral }}>
                      {selectedDay.neutral_count || 0} neutres
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.danger }}>
                      {selectedDay.negative_count || 0} negatifs
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Mentions list */}
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                Mentions du jour ({dayMentions.length})
              </Typography>

              {loadingMentions ? (
                <Box sx={{ py: 4 }}>
                  <LinearProgress sx={{ mb: 2 }} />
                  <Typography variant="body2" color="textSecondary" textAlign="center">
                    Chargement des mentions...
                  </Typography>
                </Box>
              ) : dayMentions.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: 500, overflow: 'auto' }}>
                  {dayMentions.map((mention, idx) => (
                    <MentionCard
                      key={idx}
                      mention={mention}
                      brandKeywords={currentBrand?.keywords}
                      compact={false}
                    />
                  ))}
                </Box>
              ) : (
                <Box sx={{ py: 4, textAlign: 'center', bgcolor: colors.gray50, borderRadius: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Aucune mention trouvee pour ce jour.
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Les donnees peuvent avoir ete agregees ou les mentions individuelles ne sont pas disponibles.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ============================================================= */}
      {/*  DIALOG: Report Detail (Rapport tab)                           */}
      {/* ============================================================= */}
      <Dialog
        open={reportDetailOpen}
        onClose={() => setReportDetailOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: colors.charcoal, color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Rapport du {selectedReport?.report_date && formatDate(selectedReport.report_date)}
            </Typography>
            {selectedReport && renderStatusBadge(selectedReport.status, selectedReport.status_color)}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedReport?.full_report && (
            <Box>
              {/* Executive Summary */}
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: colors.charcoal, mb: 1 }}>
                Synthese Executive
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {selectedReport.full_report.executive_summary?.summary_text}
              </Typography>

              {/* Objectives Analysis */}
              {selectedReport.full_report.objectives_analysis?.has_objectives && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: colors.charcoal, mb: 2 }}>
                    Analyse des Objectifs
                  </Typography>
                  <Alert
                    severity={
                      selectedReport.full_report.objectives_analysis.overall_progress_pct >= 75 ? 'success' :
                      selectedReport.full_report.objectives_analysis.overall_progress_pct >= 50 ? 'info' : 'warning'
                    }
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="body2">
                      {selectedReport.full_report.objectives_analysis.summary}
                    </Typography>
                  </Alert>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {selectedReport.full_report.objectives_analysis.objectives?.map((obj, idx) => (
                      <Grid item xs={12} sm={6} md={4} key={idx}>
                        <Card
                          variant="outlined"
                          sx={{
                            p: 2,
                            borderColor: obj.status === 'achieved' ? colors.success :
                                        obj.status === 'at_risk' ? colors.danger : colors.gray200,
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: colors.charcoal }}>
                              {obj.name}
                            </Typography>
                            <Chip
                              label={obj.status_label}
                              size="small"
                              sx={{
                                bgcolor: obj.status === 'achieved' ? `${colors.success}20` :
                                         obj.status === 'on_track' ? `${colors.success}10` :
                                         obj.status === 'at_risk' ? `${colors.danger}20` : `${colors.warning}20`,
                                color: obj.status === 'achieved' ? colors.success :
                                       obj.status === 'on_track' ? colors.success :
                                       obj.status === 'at_risk' ? colors.danger : colors.warning,
                                fontSize: '0.65rem',
                              }}
                            />
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, obj.progress_pct || 0)}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              bgcolor: colors.gray100,
                              mb: 1,
                              '& .MuiLinearProgress-bar': {
                                bgcolor: obj.status === 'achieved' ? colors.success :
                                         obj.status === 'at_risk' ? colors.danger : colors.charcoal,
                              },
                            }}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" color="textSecondary">
                              Actuel: {obj.current?.toFixed(1)} {obj.unit}
                            </Typography>
                            <Typography variant="caption" fontWeight="bold">
                              Cible: {obj.target?.toFixed(1)} {obj.unit}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mt: 0.5,
                              textAlign: 'right',
                              fontWeight: 'bold',
                              color: obj.progress_pct >= 100 ? colors.success :
                                     obj.progress_pct >= 50 ? colors.charcoal : colors.danger,
                            }}
                          >
                            {obj.progress_pct?.toFixed(0)}%
                          </Typography>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              <Divider sx={{ my: 2 }} />

              {/* SWOT */}
              {selectedReport.full_report.swot && (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: colors.charcoal, mb: 2 }}>
                    Analyse SWOT
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {[
                      { key: 'strengths', label: 'Forces', color: colors.success },
                      { key: 'weaknesses', label: 'Faiblesses', color: colors.danger },
                      { key: 'opportunities', label: 'Opportunites', color: colors.charcoal },
                      { key: 'threats', label: 'Menaces', color: colors.warning },
                    ].map(({ key, label, color }) => (
                      <Grid item xs={6} key={key}>
                        <Card variant="outlined" sx={{ borderColor: color, p: 2 }}>
                          <Typography variant="subtitle2" sx={{ color, fontWeight: 'bold', mb: 1 }}>
                            {label}
                          </Typography>
                          <List dense>
                            {selectedReport.full_report.swot[key]?.map((item, i) => (
                              <ListItem key={i} sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 24 }}>
                                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary={typeof item === 'string' ? item : item.point}
                                  secondary={typeof item === 'object' ? item.detail : undefined}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                  secondaryTypographyProps={{ variant: 'caption' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Recommendations */}
              {selectedReport.full_report.recommendations?.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: colors.charcoal, mb: 2 }}>
                    Recommandations
                  </Typography>
                  <List>
                    {selectedReport.full_report.recommendations.map((rec, idx) => {
                      const priorityColor = rec.priority <= 2 ? colors.danger :
                                           rec.priority <= 3 ? colors.warning : colors.success;
                      const priorityLabel = rec.priority <= 2 ? 'Urgent' :
                                           rec.priority <= 3 ? 'Important' : 'Normal';
                      return (
                        <ListItem
                          key={idx}
                          sx={{
                            bgcolor: colors.gray50,
                            borderRadius: 2,
                            mb: 1,
                            border: `1px solid ${priorityColor}40`,
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mb: 1 }}>
                            <Typography variant="body1" fontWeight="bold">
                              {rec.title}
                            </Typography>
                            <Chip
                              label={priorityLabel}
                              size="small"
                              sx={{ bgcolor: `${priorityColor}20`, color: priorityColor }}
                            />
                            <Chip label={rec.category} size="small" variant="outlined" sx={{ ml: 'auto' }} />
                          </Box>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                            {rec.description}
                          </Typography>
                          {rec.actions?.length > 0 && (
                            <Box sx={{ pl: 2, mb: 1 }}>
                              {rec.actions.map((action, aIdx) => (
                                <Typography key={aIdx} variant="body2">
                                  - {action}
                                </Typography>
                              ))}
                            </Box>
                          )}
                          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                            {rec.timeline && (
                              <Typography variant="caption" color="textSecondary">
                                Delai: {rec.timeline}
                              </Typography>
                            )}
                            {rec.expected_impact && (
                              <Typography variant="caption" color="textSecondary">
                                Impact: {rec.expected_impact}
                              </Typography>
                            )}
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReportDetailOpen(false)}>
            Fermer
          </Button>
          {selectedReport && (
            <Button
              variant="contained"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => handleDownloadPdf(selectedReport.report_id)}
              sx={{ bgcolor: colors.charcoal, color: 'white', '&:hover': { bgcolor: colors.slate } }}
            >
              Telecharger PDF
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
    </PageTransition>
  );
}

export default HistoriqueReportPage;
